import { useMemo, useState } from "react";
import type { CreateMoodEntryRequest, MoodGoal, MoodType } from "@repo/shared-types";
import { GOAL_LABELS, MOOD_LABELS } from "../lib/mood";
import { useMoodDiscovery } from "../hooks/useMood";
import { buttonStyles, displayTitleStyle, eyebrowStyle, fieldStyle as sharedFieldStyle, heroPanelStyle, panelStyle, subtleText, tokens } from "../lib/ui";

const moodOrder: MoodType[] = [
  "peaceful",
  "energetic",
  "focused",
  "tired",
  "anxious",
  "stressed",
  "sad",
  "scattered",
];

const goalOrder: MoodGoal[] = [
  "sleep",
  "anxiety",
  "focus",
  "energy",
  "peace",
  "wisdom",
  "stress-relief",
];

export function MoodPage() {
  const { history, recommendations, loading, saving, error, submitAssessment } = useMoodDiscovery();
  const [step, setStep] = useState(0);
  const [form, setForm] = useState<CreateMoodEntryRequest>({
    mood: "peaceful",
    energy_level: 3,
    focus_level: 3,
    stress_level: 2,
    goal: "peace",
    notes: "",
  });

  const progress = useMemo(() => ((step + 1) / 5) * 100, [step]);

  async function completeAssessment() {
    await submitAssessment(form);
    setStep(4);
  }

  if (loading) return <div className="page-loading">Loading mood discovery…</div>;

  return (
    <div className="inner-page__container" style={{ gap: "1.5rem" }}>
      <section style={heroStyle}>
        <div>
          <div style={eyebrowStyle}>Mood Discovery</div>
          <h2 style={headingStyle}>Shape today’s practice around how you actually feel.</h2>
          <p style={subtleStyle}>
            A guided check-in inspired by the source app, now powered by worker-backed storage and recommendation endpoints.
          </p>
        </div>
      </section>

      {error && <p style={{ color: tokens.color.error, background: tokens.color.errorLight, border: "1px solid #fecaca", borderRadius: tokens.radius.md, padding: "0.8rem 1rem", margin: 0 }}>{error}</p>}

      <section style={panelStyle}>
        <div style={progressHeaderStyle}>
          <strong>Assessment progress</strong>
          <span>{Math.round(progress)}%</span>
        </div>
        <div style={progressRailStyle}>
          <div style={{ ...progressFillStyle, width: `${progress}%` }} />
        </div>
      </section>

      {step === 0 && (
        <section style={panelStyle}>
          <h3 style={panelTitleStyle}>How are you feeling?</h3>
          <p style={subtleStyle}>Choose the emotional state that best matches your current experience.</p>
          <div style={moodGridStyle}>
            {moodOrder.map((mood) => {
              const meta = MOOD_LABELS[mood];
              const selected = form.mood === mood;
              return (
                <button
                  key={mood}
                  onClick={() => {
                    setForm((prev) => ({ ...prev, mood }));
                    setStep(1);
                  }}
                  style={{
                    ...selectCardStyle,
                    borderColor: selected ? meta.color : "#e5e7eb",
                    background: selected ? `${meta.color}14` : "#fff",
                  }}
                >
                  <span style={{ fontSize: "2rem" }}>{meta.emoji}</span>
                  <strong>{meta.label}</strong>
                </button>
              );
            })}
          </div>
        </section>
      )}

      {step === 1 && (
        <section style={panelStyle}>
          <h3 style={panelTitleStyle}>What’s your energy level?</h3>
          <ScaleSelector
            labels={["Very low", "Low", "Steady", "High", "Very high"]}
            value={form.energy_level}
            onBack={() => setStep(0)}
            onSelect={(value) => {
              setForm((prev) => ({ ...prev, energy_level: value }));
              setStep(2);
            }}
          />
        </section>
      )}

      {step === 2 && (
        <section style={panelStyle}>
          <h3 style={panelTitleStyle}>How focused and how stressed do you feel?</h3>
          <DualScaleEditor
            focus={form.focus_level}
            stress={form.stress_level}
            setFocus={(value) => setForm((prev) => ({ ...prev, focus_level: value }))}
            setStress={(value) => setForm((prev) => ({ ...prev, stress_level: value }))}
            onBack={() => setStep(1)}
            onNext={() => setStep(3)}
          />
        </section>
      )}

      {step === 3 && (
        <section style={panelStyle}>
          <h3 style={panelTitleStyle}>What would help most right now?</h3>
          <p style={subtleStyle}>Pick an intention and add a note if you want more context.</p>
          <div style={goalGridStyle}>
            {goalOrder.map((goal) => {
              const meta = GOAL_LABELS[goal];
              const selected = form.goal === goal;
              return (
                <button
                  key={goal}
                  onClick={() => setForm((prev) => ({ ...prev, goal }))}
                  style={{
                    ...selectCardStyle,
                    borderColor: selected ? "#a16207" : "#e5e7eb",
                    background: selected ? "#fffbeb" : "#fff",
                  }}
                >
                  <span style={{ fontSize: "1.8rem" }}>{meta.icon}</span>
                  <strong>{meta.label}</strong>
                </button>
              );
            })}
          </div>
          <textarea
            value={form.notes ?? ""}
            onChange={(e) => setForm((prev) => ({ ...prev, notes: e.target.value }))}
            placeholder="Optional note about what’s happening for you today"
            style={{ ...sharedFieldStyle, width: "100%", minHeight: 96, marginTop: "1rem", resize: "vertical" }}
          />
          <div style={{ display: "flex", gap: "0.75rem", marginTop: "1rem" }}>
            <button onClick={() => setStep(2)} style={secondaryButtonStyle}>
              Back
            </button>
            <button onClick={() => void completeAssessment()} disabled={saving} style={primaryButtonStyle}>
              {saving ? "Analyzing..." : "Generate Program"}
            </button>
          </div>
        </section>
      )}

      {step === 4 && (
        <section style={panelStyle}>
          <h3 style={panelTitleStyle}>Your recommended flow</h3>
          {!recommendations ? (
            <p style={subtleStyle}>No recommendations available yet.</p>
          ) : (
            <div style={{ display: "grid", gap: "0.75rem" }}>
              {recommendations.recommendations.map((item) => (
                <article key={`${item.type}-${item.title}`} style={recommendationCardStyle}>
                  <div style={{ display: "flex", justifyContent: "space-between", gap: "1rem" }}>
                    <div>
                      <div style={{ textTransform: "uppercase", letterSpacing: "0.08em", fontSize: "0.75rem", color: "#a16207" }}>
                        {item.type}
                      </div>
                      <h4 style={{ margin: "0.2rem 0" }}>{item.title}</h4>
                    </div>
                    <strong>{item.duration} min</strong>
                  </div>
                  <p style={{ margin: "0.3rem 0", color: "#374151" }}>{item.description}</p>
                  <small style={{ color: "#6b7280" }}>{item.reasoning}</small>
                </article>
              ))}
            </div>
          )}
          <div style={{ display: "flex", gap: "0.75rem", marginTop: "1rem" }}>
            <button onClick={() => setStep(0)} style={secondaryButtonStyle}>Start another check-in</button>
          </div>
        </section>
      )}

      <section style={panelStyle}>
        <h3 style={panelTitleStyle}>Recent mood history</h3>
        {history.length === 0 ? (
          <p style={subtleStyle}>No mood check-ins recorded yet.</p>
        ) : (
          <div style={{ display: "grid", gap: "0.75rem" }}>
            {history.slice(0, 8).map((entry) => (
              <div key={entry.id} style={historyRowStyle}>
                <div>
                  <strong>{MOOD_LABELS[entry.mood].emoji} {MOOD_LABELS[entry.mood].label}</strong>
                  <div style={subtleStyle}>{GOAL_LABELS[entry.goal].label}</div>
                </div>
                <div style={{ textAlign: "right" }}>
                  <div style={{ fontWeight: 700 }}>E{entry.energy_level} · F{entry.focus_level} · S{entry.stress_level}</div>
                  <div style={subtleStyle}>{new Date(entry.created_at).toLocaleString()}</div>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

function ScaleSelector({
  labels,
  value,
  onBack,
  onSelect,
}: {
  labels: string[];
  value: number;
  onBack: () => void;
  onSelect: (value: 1 | 2 | 3 | 4 | 5) => void;
}) {
  return (
    <>
      <div style={{ display: "grid", gap: "0.75rem", marginTop: "1rem" }}>
        {[1, 2, 3, 4, 5].map((level, index) => (
          <button
            key={level}
            onClick={() => onSelect(level as 1 | 2 | 3 | 4 | 5)}
            style={{
              ...scaleRowStyle,
              borderColor: value === level ? "#a16207" : "#e5e7eb",
              background: value === level ? "#fffbeb" : "#fff",
            }}
          >
            <span style={badgeStyle}>{level}</span>
            <span>{labels[index]}</span>
          </button>
        ))}
      </div>
      <button onClick={onBack} style={{ ...secondaryButtonStyle, marginTop: "1rem" }}>Back</button>
    </>
  );
}

function DualScaleEditor({
  focus,
  stress,
  setFocus,
  setStress,
  onBack,
  onNext,
}: {
  focus: number;
  stress: number;
  setFocus: (value: 1 | 2 | 3 | 4 | 5) => void;
  setStress: (value: 1 | 2 | 3 | 4 | 5) => void;
  onBack: () => void;
  onNext: () => void;
}) {
  return (
    <>
      <ScaleBlock title="Focus" left="Scattered" right="Locked in" value={focus} onChange={setFocus} />
      <ScaleBlock title="Stress" left="Calm" right="Overwhelmed" value={stress} onChange={setStress} />
      <div style={{ display: "flex", gap: "0.75rem", marginTop: "1rem" }}>
        <button onClick={onBack} style={secondaryButtonStyle}>Back</button>
        <button onClick={onNext} style={primaryButtonStyle}>Continue</button>
      </div>
    </>
  );
}

function ScaleBlock({
  title,
  left,
  right,
  value,
  onChange,
}: {
  title: string;
  left: string;
  right: string;
  value: number;
  onChange: (value: 1 | 2 | 3 | 4 | 5) => void;
}) {
  return (
    <div style={{ marginTop: "1rem" }}>
      <div style={{ fontWeight: 700, marginBottom: "0.5rem" }}>{title}</div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: "0.5rem" }}>
        {[1, 2, 3, 4, 5].map((level) => (
          <button
            key={level}
            onClick={() => onChange(level as 1 | 2 | 3 | 4 | 5)}
            style={{
              ...chipStyle,
              borderColor: value === level ? "#a16207" : "#d1d5db",
              background: value === level ? "#fffbeb" : "#fff",
            }}
          >
            {level}
          </button>
        ))}
      </div>
      <div style={{ display: "flex", justifyContent: "space-between", color: "#6b7280", marginTop: "0.35rem", fontSize: "0.85rem" }}>
        <span>{left}</span>
        <span>{right}</span>
      </div>
    </div>
  );
}

const heroStyle: React.CSSProperties = {
  ...heroPanelStyle,
  padding: tokens.space.lg,
};

const headingStyle: React.CSSProperties = {
  ...displayTitleStyle,
  margin: "0.35rem 0",
  fontSize: tokens.fontSize["3xl"],
  lineHeight: 1.1,
};

const subtleStyle: React.CSSProperties = subtleText;

const panelTitleStyle: React.CSSProperties = {
  margin: 0,
  fontSize: "1.15rem",
};

const progressHeaderStyle: React.CSSProperties = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  marginBottom: "0.75rem",
};

const progressRailStyle: React.CSSProperties = {
  height: 12,
  borderRadius: 999,
  overflow: "hidden",
  background: "#e5e7eb",
};

const progressFillStyle: React.CSSProperties = {
  height: "100%",
  borderRadius: 999,
  background: "linear-gradient(90deg, #2563eb 0%, #f59e0b 100%)",
};

const moodGridStyle: React.CSSProperties = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(130px, 1fr))",
  gap: "0.75rem",
  marginTop: "1rem",
};

const goalGridStyle: React.CSSProperties = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))",
  gap: "0.75rem",
  marginTop: "1rem",
};

const selectCardStyle: React.CSSProperties = {
  border: "1px solid #e5e7eb",
  borderRadius: 14,
  background: "#fff",
  padding: "1rem",
  display: "grid",
  gap: "0.5rem",
  justifyItems: "center",
  cursor: "pointer",
};

const recommendationCardStyle: React.CSSProperties = {
  border: "1px solid #e5e7eb",
  borderRadius: 14,
  padding: "1rem",
  background: "#fafaf9",
};

const historyRowStyle: React.CSSProperties = {
  display: "flex",
  justifyContent: "space-between",
  gap: "1rem",
  padding: "0.9rem 0",
  borderBottom: "1px solid #f3f4f6",
};

const primaryButtonStyle: React.CSSProperties = {
  ...buttonStyles.primary,
};

const secondaryButtonStyle: React.CSSProperties = {
  ...buttonStyles.secondary,
};

const scaleRowStyle: React.CSSProperties = {
  border: "1px solid #e5e7eb",
  borderRadius: 14,
  padding: "0.9rem",
  display: "flex",
  alignItems: "center",
  gap: "0.75rem",
  cursor: "pointer",
  background: "#fff",
};

const badgeStyle: React.CSSProperties = {
  width: 28,
  height: 28,
  display: "grid",
  placeItems: "center",
  borderRadius: 999,
  background: "#f3f4f6",
  fontWeight: 700,
};

const chipStyle: React.CSSProperties = {
  border: "1px solid #d1d5db",
  borderRadius: 10,
  padding: "0.8rem 0",
  cursor: "pointer",
  background: "#fff",
  fontWeight: 700,
};
